import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Generic export button:
 *   <ExportButton path="exports/financial-report" filenameBase="financial_report" />
 * Calls GET {API_URL}/api/{path}?fmt=excel|pdf with bearer token, downloads blob.
 */
const ExportButton = ({ path, filenameBase = 'export', label = 'Export', extraParams = {}, testId }) => {
  const [busy, setBusy] = useState(false);

  const download = async (fmt) => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ fmt, ...extraParams }).toString();
      const res = await axios.get(`${API_URL}/api/${path}?${params}`, {
        responseType: 'blob',
      });
      const ext = fmt === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data], {
        type: fmt === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `${filenameBase}_${ts}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Not allowed to export' : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={busy} data-testid={testId || 'export-btn'}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          {label}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download('excel')} data-testid="export-excel-option">
          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
          Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => download('pdf')} data-testid="export-pdf-option">
          <FileText className="h-4 w-4 mr-2 text-red-600" />
          PDF (.pdf)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;
